import { createId } from '@paralleldrive/cuid2';
import { timestamp } from './utils/Time';
import { View } from './View';
import type { StudyEventContext } from './type';
import { AIDTJSONSchemaPlayedMedia } from '@ui/utils/ActivityManager/KERIS-xapi/playedMedia';
import { AIDTJSONSchemaSubmittedAssessment } from '@ui/utils/ActivityManager/KERIS-xapi/submittedAssessment';
import { AIDTJSONSchemaGaveAssignment } from '@ui/utils/ActivityManager/KERIS-xapi/gaveAssignment';
import { AIDTJSONSchemaFinishedAssignment } from '@ui/utils/ActivityManager/KERIS-xapi/finishedAssignment';
import { AIDTJSONSchemaViewedImage } from '@ui/utils/ActivityManager/KERIS-xapi/viewedImage';
import { AIDTJSONSchemaReadDocument } from '@ui/utils/ActivityManager/KERIS-xapi/readDocument';
import { AIDTJSONSchemaDidPractice } from '@ui/utils/ActivityManager/KERIS-xapi/didPractice';
import { AIDTJSONSchemaLearnedEtcContent } from '@ui/utils/ActivityManager/KERIS-xapi/learnedETCContent';
import { AIDTJSONSchemaAskQuestion } from '@ui/utils/ActivityManager/KERIS-xapi/askQuestion';
import { AIDTJSONSchemaParticipatedSocialLearning } from '@ui/utils/ActivityManager/KERIS-xapi/participatedSocialLearning';
import { AIDTJSONSchemaReorganizedClass } from '@ui/utils/ActivityManager/KERIS-xapi/reorganizedClass';

export type InternalAction<T = any> = {
  internal: true;
} & T;
export type NonInternalAction<T = any> = {
  internal?: undefined;
} & T;

export type OmitInternalAction<ActionData extends any> = ActionData extends InternalAction
  ? never
  : ActionData;

export function isInternalAction(action: any): action is InternalAction {
  return action.internal === true;
}

export type XApiActionType =
  | 'PLAYED_MEDIA'
  | 'SUBMITTED_ASSESSMENT'
  | 'GAVE_ASSIGNMENT'
  | 'FINISHED_ASSIGNMENT'
  | 'VIEWED_IMAGE'
  | 'READ_DOCUMENT'
  | 'DID_PRACTICE'
  | 'LEARNED_ETC_CONTENT'
  | 'ASKED_QUESTION'
  | 'PARTICIPATED_SOCIAL_LEARNING'
  | 'REORGANIZED_CLASS';

export interface XApiActionPayloads {
  PLAYED_MEDIA: AIDTJSONSchemaPlayedMedia;
  SUBMITTED_ASSESSMENT: AIDTJSONSchemaSubmittedAssessment;
  GAVE_ASSIGNMENT: AIDTJSONSchemaGaveAssignment;
  FINISHED_ASSIGNMENT: AIDTJSONSchemaFinishedAssignment;
  VIEWED_IMAGE: AIDTJSONSchemaViewedImage;
  READ_DOCUMENT: AIDTJSONSchemaReadDocument;
  DID_PRACTICE: AIDTJSONSchemaDidPractice;
  LEARNED_ETC_CONTENT: AIDTJSONSchemaLearnedEtcContent;
  ASKED_QUESTION: AIDTJSONSchemaAskQuestion;
  PARTICIPATED_SOCIAL_LEARNING: AIDTJSONSchemaParticipatedSocialLearning;
  REORGANIZED_CLASS: AIDTJSONSchemaReorganizedClass;
}

export type XApiActionData<T extends XApiActionType = XApiActionType> = T extends keyof XApiActionPayloads
  ? {
      type: T;
      payload: XApiActionPayloads[T];
    }
  : never;

export type ExternalActionData =
  | ({ type: 'SUBMIT' } & StudyEventContext)
  | ({ type: 'FINISH_EVALUATION' } & StudyEventContext)
  | { type: 'FOCUS_IN' }
  | { type: 'FOCUS_OUT' }
  | { type: 'SESSION_START' }
  | { type: 'SESSION_EXPIRATION' }
  | { type: 'INITIAL_ROUTE'; view: View }
  | { type: 'ROUTE_CHANGE'; view: View };

export type DefaultActionData =
  | ExternalActionData
  | InternalAction<{ type: '__INTERNAL_STUDY_TIME_EXCHNAGE' }>
  | XApiActionData;

export interface ActionContext<ActionData extends DefaultActionData = DefaultActionData> {
  parentSpanId?: string;
  data: ActionData;
  activitySessionId?: string;
  view?: View;
  userId?: string;
}

export class Action<ActionData extends DefaultActionData = DefaultActionData> {
  readonly actionId: string;
  readonly timestamp: number;
  readonly parentSpanId?: string;
  readonly activitySessionId?: string;
  data: ActionData;
  view?: View;
  userId?: string;

  constructor(actionContext: ActionContext<ActionData>) {
    this.actionId = createId();
    this.timestamp = timestamp();
    this.parentSpanId = actionContext?.parentSpanId;
    this.activitySessionId = actionContext?.activitySessionId;
    this.data = actionContext.data;
    this.view = actionContext?.view;
    this.userId = actionContext?.userId;
  }
}
